import { formatReadableName } from "./naming";

/**
 * 상대 분포(분위수) 기반 색상 팔레트
 * - NORMAL: 정상값 전용
 * - LOW/MID/HIGH/TOP: Q1/Q2/Q3 기준으로 상대 구간 색상
 */
export const REL_COLORS = {
  NORMAL: "#0DBD50", // 초록 (정상)
  LOW: "#EAEAEA", // 회색 (낮음, ≤ Q1)
  MID: "#FFC800", // 노랑 (≤ Q2)
  HIGH: "#FF9408", // 주황 (≤ Q3)
  TOP: "#FF3C3C", // 빨강 (> Q3)
};

/**
 * 분위수 계산
 * @param {number[]} arr - 숫자 배열
 * @param {number} q - 분위(0~1)
 * @returns {number} q 분위 값
 */
export function quantile(arr, q) {
  if (!arr.length) return 0;

  const a = [...arr].sort((x, y) => x - y);
  const pos = (a.length - 1) * q;
  const base = Math.floor(pos);
  const rest = pos - base;

  return a[base] + (a[base + 1] - (a[base] ?? 0)) * (rest || 0);
}

// Backend/legacy payloads can differ:
// - legacy: { ratioPercent: number(0-100) }
// - current BE: { avgConfidence: number(0-100) }
//   BE normalizeSummaryPercentages()가 항상 0-100 범위로 정규화하여 전송
const readRatioPercent = (item) => {
  const direct =
    item?.ratioPercent ?? item?.ratio ?? item?.percentage ?? item?.percent;
  if (direct != null) {
    const n = Number(direct);
    return Number.isFinite(n) ? n : 0;
  }

  const conf =
    item?.avgConfidence ?? item?.avgValue ?? item?.confidence ?? item?.value;
  const n = Number(conf);
  if (!Number.isFinite(n)) return 0;

  return n;
};

// 병해충 진단 결과를 그래프 형식에 맞게 변환
// 색상: 상대 분포(Q1/Q2/Q3) 기반
// 정렬: DISEASE → PEST → NORMAL, 같은 그룹은 비율 내림차순
export const getChartDataFromApi = (apiItems = [], types = []) => {
  if (!Array.isArray(apiItems) || !apiItems.length) return [];

  const validItems = apiItems.filter((item) => {
    if (!item || typeof item.nameEn !== "string") {
      console.warn("Invalid diagnosis item filtered out:", item);
      return false;
    }
    return true;
  });
  if (!validItems.length) return [];

  // NORMAL 제외한 값으로 분포(Q1/Q2/Q3) 계산
  const nonNormals = validItems.filter((i) => i.nameEn !== "NORMAL");
  const values = nonNormals.map((i) => readRatioPercent(i));

  const max = Math.max(...values, 0);
  const min = Math.min(...values, 0);
  const Q1 = quantile(values, 0.25);
  const Q2 = quantile(values, 0.5);
  const Q3 = quantile(values, 0.75);

  // 스프레드가 작으면(예: < 5%) 상대 차이가 거의 없으므로 전부 회색 처리
  const lowSpread = max - min < 5;

  // 상대 1위(강조 용도, 색상은 아님)
  const top = nonNormals.sort(
    (a, b) => readRatioPercent(b) - readRatioPercent(a)
  )[0];

  /**
   * 상대 분포 기반 색상 선택
   * - NORMAL은 항상 초록
   * - 분포가 좁으면 모두 회색
   * - 아니면 Q1/Q2/Q3 기준으로 LOW/MID/HIGH/TOP
   */
  const pickColor = (pct, isNormal) => {
    if (isNormal) return REL_COLORS.NORMAL;
    if (lowSpread) return REL_COLORS.LOW;

    if (pct <= Q1) return REL_COLORS.LOW;
    if (pct <= Q2) return REL_COLORS.MID;
    if (pct <= Q3) return REL_COLORS.HIGH;

    return REL_COLORS.TOP;
  };

  // 카테고리 매핑: NORMAL 강제, PEST만 신뢰(나머지는 DISEASE)
  const findCategory = (item) => {
    if (item.nameEn === "NORMAL") return "NORMAL";
    const t = types.find((x) => x.nameEn === item.nameEn);
    return t?.category === "PEST" ? "PEST" : "DISEASE";
  };

  // 우선순위: DISEASE(1) < PEST(2) < NORMAL(3)
  const priority = (cat, code) => {
    if (code === "NORMAL" || cat === "NORMAL") return 3;
    if (cat === "PEST") return 2;
    return 1;
  };

  // 각 데이터 변환
  const chartData = validItems.map((item) => {
    const percentage = Number(readRatioPercent(item).toFixed(1));
    const isNormal = item.nameEn === "NORMAL";
    const category = findCategory(item);
    const color = pickColor(percentage, isNormal);
    const accent =
      !isNormal && top && item.nameEn === top.nameEn ? "top" : null; // 선택

    return {
      name: formatReadableName(item.nameEn || ""), // CABBAGE_SOFT_ROT → Cabbage Soft Rot
      dataKey: item.nameEn,
      percentage,
      color,
      category,
      accent,
      ...item,
    };
  });

  // 정렬: 우선순위 → 비율 내림 → 이름
  return chartData.sort((a, b) => {
    const pa = priority(a.category, a.nameEn);
    const pb = priority(b.category, b.nameEn);

    if (pa !== pb) return pa - pb;
    if (b.percentage !== a.percentage) return b.percentage - a.percentage;

    return a.name.localeCompare(b.name);
  });
};
